import { Inject, Injectable, Optional } from '@angular/core';
import { CustomerContact, CustomerContactsApiService } from 'src/app/api';
import { Observable, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { PerfilService } from '../ajs-perfil.service';
import { CacheableGet } from './cacheable-get';
import { FIVE_MINUTES_IN_MILLIS, PROFILE_CACHE_TIME } from './consts';

@Injectable({
  providedIn: 'root'
})
export class ProfileContactsService {
  private getRequest: CacheableGet<CustomerContact[]>;

  private get currentUserId() {
    const user = this.perfilService.getCacheUserData();

    return user && user.id || 0;
  }

  public get shouldUpdate() {
    return this.getRequest.shouldUpdate;
  }

  constructor(
    private customerContactsApiService: CustomerContactsApiService,
    private perfilService: PerfilService,
    @Optional() @Inject(PROFILE_CACHE_TIME) cacheTime: number,
  ) {
    if (!cacheTime) {
      cacheTime = FIVE_MINUTES_IN_MILLIS;
    }

    this.getRequest = new CacheableGet<CustomerContact[]>(
      cacheTime,
      () => this.customerContactsApiService.getCustomerContacts(this.currentUserId),
    );
  }

  /**
   * Obtém a lista de contatos para o usuário logado atualmente
   */
  public getContacts(): Observable<CustomerContact[]> {
    return this.getRequest.get();
  }

  /**
   * Adiciona um novo contato para o usuário logado
   *
   * @param contactData Dados do contato a ser criado
   */
  public createContact(contactData: CustomerContact): Observable<CustomerContact> {
    return this.customerContactsApiService.createCustomerContact(this.currentUserId, contactData).pipe(
      tap(() => this.getRequest.markForUpdate()),
    );
  }

  /**
   * Altera um contato para o usuário logado
   *
   * @param contactData Dados do contato a ser alterado
   */
  public updateContact(contactData: CustomerContact): Observable<CustomerContact> {
    if (!contactData.id) {
      return throwError(new Error('Contato inválido'));
    }

    return this.customerContactsApiService.updateCustomerContact(this.currentUserId, contactData.id, contactData).pipe(
      tap(() => this.getRequest.markForUpdate()),
    );
  }

  /**
   * Remove um contato para o usuário logado
   *
   * @param contactData Dados do contato a ser removido
   */
  public deleteContact(contactData: CustomerContact): Observable<CustomerContact> {
    if (!contactData.id) {
      return throwError(new Error('Contato inválido'));
    }

    return this.customerContactsApiService.deleteCustomerContact(this.currentUserId, contactData.id).pipe(
      tap(() => this.getRequest.markForUpdate()),
    );
  }
}
